'use client'

/**
 * OnlineCounter — Live-Anzeige der aktuell angemeldeten Piloten.
 *
 * Verhalten:
 *  - R103.2 (2026-06-04, T+1 nach Launch, Stephan-Wunsch):
 *    Pille fuer ALLE sichtbar (auch nicht-eingeloggte) — anonyme Zahl,
 *    konsistent zu Privacy-Leitsatz §14d/f.
 *  - Eingeloggte (Admins + Pro/Premium + Free): senden alle 30 s einen
 *    Heartbeat (POST /api/session/heartbeat) und lesen Count daraus.
 *  - Nicht-eingeloggte: pollen Count alle 30 s aus /api/public/stats.
 *  - Klick auf die Pille:
 *      - Admin -> Popover mit Namensliste (GET /api/admin/online-users).
 *      - sonst kein Detail (Privacy by Design).
 *  - R96 (2026-05-21): Admin-Klick auf den Badge öffnet ein Popup
 *    mit der Liste aller aktuell online Piloten (Name + letzter Heartbeat).
 *  - Funktioniert auch in PWA-Background-Tabs solange das Tab offen ist.
 */

import { useEffect, useRef, useState } from 'react'
import { useSession } from 'next-auth/react'
import { Users, X } from 'lucide-react'
import { useTranslation } from '@/i18n/client'

const HEARTBEAT_MS = 30_000

type OnlineUser = {
  id: string
  name: string | null
  displayName: string | null
  slug: string | null
  role: string
  secondsAgo: number | null
}

export function OnlineCounter() {
  const { t } = useTranslation()
  const { data: session, status } = useSession()
  const [count, setCount] = useState<number | null>(null)
  const [visitors, setVisitors] = useState<number | null>(null)
  const [open, setOpen] = useState(false)
  const [users, setUsers] = useState<OnlineUser[] | null>(null)
  const [loading, setLoading] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const isAdmin = session?.user?.role === 'ADMIN'

  useEffect(() => {
    if (status === 'loading') return
    let cancelled = false
    // r19.55.73 (Stephan-Spec 2026-07-28): Auch ausgeloggte pingen jetzt
    // /api/session/heartbeat (anonymer Visitor-Cookie). Zwei getrennte
    // Counts: Piloten (online) + anonyme Besucher (visitors).
    async function tick() {
      try {
        const res = await fetch('/api/session/heartbeat', { method: 'POST' })
        if (cancelled) return
        if (res.ok) {
          const j = await res.json()
          if (typeof j.online === 'number') setCount(j.online)
          if (typeof j.visitors === 'number') setVisitors(j.visitors)
        }
      } catch { /* silent */ }
    }
    tick()
    const id = setInterval(tick, HEARTBEAT_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [status])

  async function loadUsers() {
    setLoading(true)
    try {
      const res = await fetch('/api/admin/online-users')
      if (res.ok) {
        const j = await res.json()
        setUsers(j.users ?? [])
      }
    } catch { /* silent */ }
    setLoading(false)
  }

  useEffect(() => {
    if (!open) return
    loadUsers()
    const id = setInterval(loadUsers, HEARTBEAT_MS)
    return () => clearInterval(id)
  }, [open])

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // R103.2: Pille fuer ALLE sichtbar (auch ausgeloggt). Bei 0 verstecken
  // damit kein „0 online"-Eindruck entsteht. count === null = noch nicht geladen.
  const showPilots = count !== null && count > 0
  const showVisitors = visitors !== null && visitors > 0
  if (!showPilots && !showVisitors) return null

  return (
    <div className="relative inline-flex items-center gap-1.5" ref={popoverRef}>
      {/* r19.55.72 (Stephan-Spec 2026-07-14): Online-Pille im Header prominenter -
          groesser, kraeftigerer Puls, staerkerer Glow. Ersetzt die entfernte
          Home-Stats-Kachel "Piloten Online".
          r19.55.73 (Stephan-Spec 2026-07-28): Piloten-Pille NUR wenn > 0.
          Anonyme Besucher-Pille daneben. */}
      {showPilots && (
      <button
        type="button"
        onClick={() => { if (isAdmin) setOpen(v => !v) }}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] sm:text-[14px] font-black uppercase tracking-[.10em] transition-transform hover:scale-105 ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}
        style={{
          background: 'linear-gradient(135deg, rgba(34,197,94,0.22) 0%, rgba(16,185,129,0.28) 100%)',
          color: '#16a34a',
          border: '1.5px solid rgba(34,197,94,0.65)',
          boxShadow: '0 0 20px rgba(34,197,94,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
        }}
        data-tip={isAdmin
          ? t('onlineCounter.adminTip', { count, label: t(count === 1 ? 'onlineCounter.pilotSingular' : 'onlineCounter.pilotPlural') })
          : t('onlineCounter.userTip', { count, label: t(count === 1 ? 'onlineCounter.pilotSingular' : 'onlineCounter.pilotPlural') })}
        aria-expanded={isAdmin ? open : undefined}
        aria-haspopup={isAdmin ? 'dialog' : undefined}
      >
        <span
          className="inline-block w-2.5 h-2.5 rounded-full"
          style={{
            background: '#22c55e',
            boxShadow: '0 0 0 4px rgba(34,197,94,0.35), 0 0 12px rgba(34,197,94,0.75)',
            animation: 'fgl-online-pulse 1.6s ease-in-out infinite',
          }}
        />
        <Users className="w-4 h-4" strokeWidth={2.4} />
        <span className="tabular-nums font-black" style={{ fontSize: 'clamp(14px, 1.4vw, 18px)' }}>
          {count}
        </span>
        <span className="hidden md:inline text-[11px] font-bold opacity-90">
          {t(count === 1 ? 'onlineCounter.pilotSingular' : 'onlineCounter.pilotPlural')}
        </span>
        <style jsx>{`
          @keyframes fgl-online-pulse {
            0%, 100% {
              box-shadow: 0 0 0 4px rgba(34,197,94,0.35), 0 0 12px rgba(34,197,94,0.75);
              transform: scale(1);
            }
            50% {
              box-shadow: 0 0 0 8px rgba(34,197,94,0.10), 0 0 20px rgba(34,197,94,0.95);
              transform: scale(1.15);
            }
          }
        `}</style>
      </button>
      )}

      {/* r19.55.73 (Stephan-Spec 2026-07-28): Anonyme Besucher-Pille neben
          Piloten. Etwas dezenter (Blau statt Grün) damit klar ist wer wer ist -
          Piloten sind die "aktive Community", Besucher zeigen die Reichweite. */}
      {showVisitors && (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] sm:text-[13px] font-black uppercase tracking-[.10em]"
          style={{
            background: 'linear-gradient(135deg, rgba(59,130,246,0.16) 0%, rgba(37,99,235,0.22) 100%)',
            color: '#2563eb',
            border: '1.5px solid rgba(59,130,246,0.55)',
            boxShadow: '0 0 14px rgba(59,130,246,0.28), inset 0 1px 0 rgba(255,255,255,0.15)',
          }}
          data-tip={t('onlineCounter.visitorTip', { count: visitors })}
        >
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{
              background: '#3b82f6',
              boxShadow: '0 0 0 3px rgba(59,130,246,0.28), 0 0 10px rgba(59,130,246,0.65)',
            }}
          />
          <span className="tabular-nums font-black" style={{ fontSize: 'clamp(12px, 1.2vw, 16px)' }}>
            {visitors}
          </span>
          <span className="hidden md:inline text-[10px] font-bold opacity-90">
            {t(visitors === 1 ? 'onlineCounter.visitorSingular' : 'onlineCounter.visitorPlural')}
          </span>
        </span>
      )}

      {open && (
        <div
          role="dialog"
          aria-label={t('onlineCounter.dialogLabel')}
          className="absolute right-0 mt-2 w-72 sm:w-80 max-h-[60vh] overflow-auto rounded-2xl"
          style={{
            // 2026-06-14 (Stephan-Spec): Glas-Design analog zu /flight/[id]
            // award-chroma-Karten. Theme-aware Hintergrund + multi-layer
            // Drop-Shadow + Inset-Top-Sheen + dezenter Gold-Ambient.
            background:
              'radial-gradient(120% 60% at 50% 0%, color-mix(in oklch, var(--accent) 6%, transparent) 0%, transparent 60%), ' +
              'linear-gradient(180deg, color-mix(in oklch, var(--text) 3%, var(--award-ink-2)) 0%, var(--award-ink-2) 60%, color-mix(in oklch, var(--award-ink) 25%, var(--award-ink-2)) 100%)',
            border: '1px solid color-mix(in oklch, var(--award-line-hi) 60%, transparent)',
            backdropFilter: 'blur(14px) saturate(120%)',
            WebkitBackdropFilter: 'blur(14px) saturate(120%)',
            boxShadow:
              'inset 0 1px 0 color-mix(in oklch, var(--text) 14%, transparent), ' +
              'inset 0 -1px 0 color-mix(in oklch, var(--award-ink) 60%, transparent), ' +
              '0 1px 2px color-mix(in oklch, var(--award-ink) 30%, transparent), ' +
              '0 8px 18px -10px color-mix(in oklch, var(--award-ink) 55%, transparent), ' +
              '0 28px 56px -28px color-mix(in oklch, var(--award-ink) 70%, transparent), ' +
              '0 0 28px -14px color-mix(in oklch, var(--accent) 22%, transparent)',
            zIndex: 1000,
          }}
        >
          <div
            className="flex items-center justify-between px-3 py-2.5 border-b"
            style={{ borderColor: 'color-mix(in oklch, var(--award-line) 80%, transparent)' }}
          >
            <div
              className="text-[11px] font-black uppercase tracking-[.16em] inline-flex items-center gap-1.5"
              style={{ color: '#22c55e', textShadow: '0 0 12px rgba(34,197,94,0.45)' }}
            >
              <Users className="w-3 h-3" />
              {t('onlineCounter.buttonText', { count: count ?? 0 })}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors"
              style={{
                background: 'color-mix(in oklch, var(--text) 6%, transparent)',
                border: '1px solid color-mix(in oklch, var(--award-line) 70%, transparent)',
                color: 'var(--award-cirrus-2)',
              }}
              aria-label={t('onlineCounter.closeButton')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="p-2 text-[12px]">
            {loading && users === null && (
              <div className="px-2 py-3" style={{ color: 'var(--award-cirrus-3)' }}>{t('onlineCounter.loading')}</div>
            )}
            {users && users.length === 0 && (
              <div className="px-2 py-3" style={{ color: 'var(--award-cirrus-3)' }}>{t('onlineCounter.empty')}</div>
            )}
            {users && users.map(u => {
              const label = u.displayName || u.name || u.id.slice(0, 8)
              const sub = u.role === 'ADMIN' ? 'Admin' : (u.name && u.displayName && u.displayName !== u.name ? u.name : null)
              const since = u.secondsAgo === null ? '' : u.secondsAgo < 60 ? `${u.secondsAgo}s` : `${Math.floor(u.secondsAgo / 60)}m`
              return (
                <div
                  key={u.id}
                  className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg transition-colors"
                  style={{ background: 'transparent' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background =
                      'color-mix(in oklch, var(--text) 5%, transparent)'
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = 'transparent'
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium inline-flex items-center gap-1.5" style={{ color: 'var(--text)' }}>
                      {u.slug ? (
                        <a
                          href={`/pilot/${u.slug}`}
                          className="hover:opacity-80 transition-opacity"
                          style={{ color: 'var(--text)' }}
                        >
                          {label}
                        </a>
                      ) : label}
                      {u.role === 'ADMIN' && (
                        <span
                          className="px-1.5 py-0.5 rounded-md text-[9px] font-black uppercase tracking-[.14em]"
                          style={{
                            background:
                              'linear-gradient(180deg, color-mix(in oklch, var(--accent) 26%, transparent) 0%, color-mix(in oklch, var(--accent) 14%, transparent) 100%)',
                            color: 'var(--accent)',
                            border: '1px solid color-mix(in oklch, var(--accent) 42%, transparent)',
                            boxShadow:
                              'inset 0 1px 0 color-mix(in oklch, var(--text) 16%, transparent), ' +
                              'inset 0 -1px 0 color-mix(in oklch, var(--award-ink) 40%, transparent), ' +
                              '0 1px 1px color-mix(in oklch, var(--award-ink) 35%, transparent), ' +
                              '0 3px 8px -3px color-mix(in oklch, var(--accent) 50%, transparent)',
                          }}
                        >
                          {t('onlineCounter.adminBadge')}
                        </span>
                      )}
                    </div>
                    {sub && (
                      <div className="truncate text-[10px]" style={{ color: 'var(--award-cirrus-3)' }}>{sub}</div>
                    )}
                  </div>
                  <div className="shrink-0 text-[10px] tabular-nums" style={{ color: 'var(--award-cirrus-3)' }}>
                    {since}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
